import { create } from 'zustand';
import { FontData } from '@quicktools/shared-components/types/text';
import { ChatRole } from '@quicktools/shared-components/services/chatApi/types';
import { IFont, FontCategory } from '@quicktools/shared-components/data/fonts';
import {
  addToEditorData,
  DEFAULT_EDITOR_DATA,
} from '@quicktools/shared-components/components/domainSpecific/text/Editor';

import analytics from '../services/analytics';
import chatApi from '../services/chatApi';

interface IStateModel {
  mainText: string;
  setMainText: (s: string) => void;
  editorData: FontData;
  setEditorData: (text: FontData) => void;
  addToEditor: (text: string, font: IFont) => void;
  selectedCategory?: FontCategory;
  setSelectedCategory: (c: FontCategory) => void;
  editorExpanded: boolean;
  setEditorExpanded: (b: boolean) => void;
  currentSelectedFont: IFont;
  setCurrentSelectedFont: (f: IFont) => void;
  generateText: () => void;
  setTextIsGenerating: (v: boolean) => void;
  copiedFont: IFont;
  setCopiedFont: (f: IFont) => void;
  textIsGenerating: boolean;
  textGeneratingFailed: boolean;
  generatedText: string;
  showGenerateTextBanner: boolean;
  showGenerateTextButton: boolean;
  cleanGeneratedText: () => void;
}

const generateMessages = [
  {
    role: 'system' as ChatRole,
    content:
      "You are a text generator that generates 1 short text bit (max. 40 characters with spaces) that is funny, engaging, friendly, and useful to social media and messaging. Don't use exclamation points, periods, question marks, air quotes or other special characters.",
  },
  { role: 'user' as ChatRole, content: 'Generate a text bit' },
];

let selectionTimeout: ReturnType<typeof setTimeout>;
let copiedTimeout: ReturnType<typeof setTimeout>;

export const useStore = create<IStateModel>()((set, get) => ({
  mainText: '',
  setMainText: (mainText: string) =>
    set({
      mainText,
    }),

  editorData: DEFAULT_EDITOR_DATA,
  addToEditor: (text: string, font: IFont) => {
    const { editorData } = get();

    set({
      editorData: addToEditorData(editorData, text, font),
    });
  },
  setEditorData: (editorData: FontData) => {
    set({
      editorData,
    });
  },

  selectedCategory: undefined,
  setSelectedCategory: (selectedCategory: FontCategory) =>
    set({
      selectedCategory,
    }),

  editorExpanded: false,
  setEditorExpanded: (editorExpanded: boolean) =>
    set({
      editorExpanded,
    }),

  copiedFont: null,
  setCopiedFont: (copiedFont) => {
    clearTimeout(copiedTimeout);
    copiedTimeout = setTimeout(() => {
      set({
        copiedFont: null,
      });
    }, 3000);
    set({
      copiedFont,
    });
  },

  currentSelectedFont: null,
  setCurrentSelectedFont: (currentSelectedFont) => {
    clearTimeout(selectionTimeout);
    selectionTimeout = setTimeout(() => {
      set({
        currentSelectedFont: null,
      });
    }, 10000);

    set({
      currentSelectedFont,
    });
  },

  showGenerateTextBanner: false,
  showGenerateTextButton: true,
  generatedText: '',
  textIsGenerating: false,
  textGeneratingFailed: false,

  setTextIsGenerating: (textIsGenerating) => {
    set({
      textIsGenerating,
    });
  },

  generateText: async () => {
    analytics.sendGaEvent('page_click', {
      page_path: window.location.pathname,
      action_type: 'generate_text',
    });
    set({ textIsGenerating: true });

    try {
      const { payload } = await chatApi.getResponse({
        model: 'gpt-4o-mini',
        messages: generateMessages,
      });
      set({
        generatedText: payload.choices[0].message.content,
        textIsGenerating: false,
        showGenerateTextBanner: true,
      });
    } catch (e) {
      console.error(e);

      set({
        generatedText: '',
        textIsGenerating: false,
        textGeneratingFailed: true,
      });

      setTimeout(() => {
        set({
          textGeneratingFailed: false,
        });
      }, 5000);
    }
  },

  cleanGeneratedText: () => {
    set({
      generatedText: '',
    });
  },
}));
